import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { trackEvent } from '../utils/analytics';
import { BRAND_ASSETS_AUTHORIZED, LOGO_AVANTI_WHITE } from '../data/contact';
const NAV_ITEMS = [{
  id: 'property',
  en: 'PROPERTY',
  es: 'PROPIEDAD'
}, {
  id: 'gallery',
  en: 'GALLERY',
  es: 'GALERÍA'
}, {
  id: 'bedrooms',
  en: 'BEDROOMS',
  es: 'HABITACIONES'
}, {
  id: 'amenities',
  en: 'AMENITIES',
  es: 'AMENIDADES'
}, {
  id: 'location',
  en: 'LOCATION',
  es: 'UBICACIÓN'
}, {
  id: 'agent',
  en: 'AGENT',
  es: 'AGENTE'
}, {
  id: 'contact',
  en: 'CONTACT',
  es: 'CONTACTO'
}];
export function Header() {
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);
  return <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-200 ease-out ${scrolled || menuOpen ? 'bg-midnight/95 backdrop-blur-sm' : 'bg-gradient-to-b from-midnight/60 to-transparent'}`} data-id="element-252" data-mp="2hpojifv4u0h6">
      <div className="mx-auto flex max-w-editorial items-center justify-between gap-4 px-5 py-3 lg:px-8 lg:py-4" data-id="element-253" data-mp="35l0x6838w51r">
        <a href="#top" className="flex items-center gap-4 no-underline" data-id="element-254" data-mp="30n7q7cu4b70f">
          <span className="block" data-id="element-255" data-mp="1w60h2t429sp">
            <span className="block font-display text-2xl font-semibold uppercase leading-none tracking-[0.28em] text-white lg:text-[28px]" data-id="element-256" data-mp="1x7nm5neimqs">
              VERO
            </span>
            <span className="mt-1 block text-[9px] uppercase leading-tight tracking-[0.16em] text-white/75 lg:text-[10px]" data-id="element-257" data-mp="1rd1ml2l8sxmj">
              Veronica Gomez Santana
            </span>
            <span className="hidden text-[9px] uppercase leading-tight tracking-[0.14em] text-gold sm:block lg:text-[10px]" data-id="element-258" data-mp="1ho1ij4rvvc1h">
              {t('Affiliated with Avanti Way Realty LLC', 'Afiliada a Avanti Way Realty LLC')}
            </span>
          </span>
          {BRAND_ASSETS_AUTHORIZED && <span className="hidden h-8 items-center border-l border-white/20 pl-4 sm:flex" data-id="element-259" data-mp="1cootqehgbuw9">
              <img src={LOGO_AVANTI_WHITE} alt="Avanti Way Realty LLC." className="h-auto w-[92px] object-contain lg:w-[130px]" width={130} height={26} data-id="element-260" data-mp="yovyq6yw6sih" />
            </span>}
        </a>

        <nav aria-label={t('Main navigation', 'Navegación principal')} className="hidden xl:block" data-id="element-261" data-mp="2t13zi0xwa3m6">
          <ul className="flex items-center gap-6" data-id="element-262" data-mp="2xznlg1chnfej">
            {NAV_ITEMS.map(item => <li key={item.id} data-id="element-263" data-mp="mck0giwk08jk">
                <a href={`#${item.id}`} className="label-caps text-white/85 transition-colors duration-150 ease-out hover:text-gold" data-id="element-264" data-mp="hg3f1wgqo8a6">
                  {lang === 'en' ? item.en : item.es}
                </a>
              </li>)}
          </ul>
        </nav>

        <div className="flex items-center gap-3 lg:gap-5" data-id="element-265" data-mp="1544m10y9rgnh">
          <div className="flex items-center gap-1 text-[11px] font-medium tracking-[0.12em] text-white/70" role="group" aria-label={t('Language selector', 'Selector de idioma')} data-id="element-266" data-mp="106310aha979d">
            <button type="button" onClick={() => setLang('en')} aria-pressed={lang === 'en'} className={`px-1 transition-colors duration-150 ease-out ${lang === 'en' ? 'text-gold' : 'hover:text-white'}`} data-id="element-267" data-mp="1a3zbr16b606y">
              EN
            </button>
            <span aria-hidden="true" className="text-white/30" data-id="element-268" data-mp="230rpa3zhxuek">|</span>
            <button type="button" onClick={() => setLang('es')} aria-pressed={lang === 'es'} className={`px-1 transition-colors duration-150 ease-out ${lang === 'es' ? 'text-gold' : 'hover:text-white'}`} data-id="element-269" data-mp="27wgrkf2whpek">
              ES
            </button>
          </div>

          <a href="#contact" onClick={() => trackEvent('schedule_tour_click', {
          location: 'header'
        })} className="hidden bg-gold px-5 py-3 font-display text-[12px] font-medium uppercase tracking-[0.16em] text-midnight transition-colors duration-150 ease-out hover:bg-gold-soft md:inline-block" data-id="element-270" data-mp="1p70q9pje5sx3">
            {t('Schedule a Tour', 'Agenda una Visita')}
          </a>

          <button type="button" onClick={() => setMenuOpen(open => !open)} aria-expanded={menuOpen} aria-controls="mobile-nav" aria-label={t('Toggle navigation', 'Abrir navegación')} className="text-white xl:hidden" data-id="element-271" data-mp="15irkgcoxdof4">
            {menuOpen ? <XIcon className="h-6 w-6" data-id="element-272" data-mp="oy10thpp0i9a" /> : <MenuIcon className="h-6 w-6" data-id="element-273" data-mp="ox95m800cmjj" />}
          </button>
        </div>
      </div>

      {menuOpen && <nav id="mobile-nav" aria-label={t('Main navigation', 'Navegación principal')} className="border-t border-white/10 bg-midnight px-5 pb-6 pt-2 xl:hidden" data-id="element-274" data-mp="k23jb82p15ux">
          <ul className="divide-y divide-white/10" data-id="element-275" data-mp="a8cycd5rzdjz">
            {NAV_ITEMS.map(item => <li key={item.id} data-id="element-276" data-mp="590dtz2dmikk">
                <a href={`#${item.id}`} onClick={() => setMenuOpen(false)} className="label-caps block py-3 text-white/85" data-id="element-277" data-mp="1a1b8l44axwa">
                  {lang === 'en' ? item.en : item.es}
                </a>
              </li>)}
          </ul>
          <a href="#contact" onClick={() => {
        setMenuOpen(false);
        trackEvent('schedule_tour_click', {
          location: 'mobile_menu'
        });
      }} className="mt-5 block bg-gold px-5 py-3 text-center font-display text-[13px] font-medium uppercase tracking-[0.16em] text-midnight" data-id="element-278" data-mp="3dsu7livkf417">
            {t('Schedule a Tour', 'Agenda una Visita')}
          </a>
        </nav>}
    </header>;
}